import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import FunnelProgress from "./FunnelProgress";

/**
 * Iter-166/167 — Brief matching state between form submit + success card.
 *
 * Visualizes the actual server-side work (partner routing config lookup
 * + intake POST + quote creation) so the user feels the funnel rather
 * than getting an instant card flash. Total dwell ~2 sec per the iter-167
 * spec — long enough to feel intentional, short enough to never feel
 * like a fake delay.
 *
 * The 3 status messages rotate every ~650ms so the dwell feels productive:
 *   1. "Matching providers…"
 *   2. "Reviewing available options…"
 *   3. "Preparing comparison results…"
 *
 * The parent owns the dwell timer + calls `onComplete()` when the
 * intended dwell + the actual API call have both resolved (whichever
 * is slower). We keep the timing logic in the parent (QuoteForm /
 * IntakeShell) so this component stays a pure-presentation atom.
 */
const MATCH_MESSAGES = [
  "Matching providers…",
  "Reviewing available options…",
  "Preparing comparison results…",
];

export default function MatchingState({ compact = false }) {
  const [messageIdx, setMessageIdx] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setMessageIdx((idx) => Math.min(idx + 1, MATCH_MESSAGES.length - 1));
    }, 650);
    return () => clearInterval(id);
  }, []);

  return (
    <div
      data-testid="funnel-matching-state"
      className={`bg-white border border-slate-200 ${
        compact ? "p-5" : "p-6 md:p-8"
      }`}
    >
      <FunnelProgress step={2} state="matching" />
      <div className="flex flex-col items-center text-center py-6 sm:py-8 gap-4">
        <div className="w-12 h-12 grid place-items-center bg-[#10B981]/10 border border-[#10B981]/30 rounded-full">
          <Loader2
            className="w-6 h-6 text-[#10B981] animate-spin"
            strokeWidth={2.5}
            aria-hidden="true"
          />
        </div>
        <div className="min-h-[60px]">
          <p
            data-testid="funnel-matching-message"
            key={messageIdx /* re-key forces a re-mount → CSS fade */}
            className="font-display font-black text-xl md:text-2xl text-slate-950 leading-tight tracking-tight opacity-0 animate-[matchFadeIn_0.3s_ease-out_forwards]"
            style={{
              animationName: "matchFadeIn",
              animationDuration: "0.3s",
              animationTimingFunction: "ease-out",
              animationFillMode: "forwards",
            }}
          >
            {MATCH_MESSAGES[messageIdx]}
          </p>
          {/* Inline keyframe — tiny + scoped so we don't need to touch
              tailwind.config.js for a one-off transition. */}
          <style>{`
            @keyframes matchFadeIn {
              from { opacity: 0; transform: translateY(4px); }
              to   { opacity: 1; transform: translateY(0); }
            }
          `}</style>
          <p className="text-xs text-slate-500 mt-2 uppercase tracking-[0.18em] font-bold">
            This usually takes about 2 seconds
          </p>
        </div>
      </div>
    </div>
  );
}
